import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { api } from '../services/api';

const DefaultConfig = () => {
  const [selectedModel, setSelectedModel] = useState('');
  const [configJson, setConfigJson] = useState('');
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  const handleRestoreSelected = () => {
    if (!selectedModel) {
      setStatus({ type: 'error', message: 'Please select a model to restore' });
      return;
    }
    setConfirmRestoreOpen(true);
  };

  const handleRestoreAll = () => {
    setConfirmRestoreOpen(true);
    setSelectedModel(''); // keep UI unchanged; restoration uses "all"
  };

  const performRestore = async (modelValue) => {
    const model = (modelValue || 'all').trim();
    const isAll = model.toLowerCase() === 'all';
    setRestoreLoading(true);
    try {
      await api.restoreDefaultConfig({ model: isAll ? 'all' : model });

      if (!isAll) {
        try {
          const response = await api.getDefaultConfig(model);
          const cfg = response?.config || response;
          setConfigJson(JSON.stringify(cfg, null, 2));
          setIsJsonValid(true);
          setStatus({ type: 'success', message: `Restored and reloaded default configuration for ${model}` });
        } catch (err) {
          setStatus({ type: 'error', message: `Restored default config for ${model} but failed to reload: ${err.message || err}` });
        }
      } else {
        setStatus({ type: 'success', message: 'Restored default configuration for all models' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to restore default configuration' });
    } finally {
      setRestoreLoading(false);
      setConfirmRestoreOpen(false);
    }
  };

  const handleEditorChange = (value) => {
    setConfigJson(value);
    try {
      if (!value.trim()) {
        setIsJsonValid(false);
        setStatus({ type: 'error', message: 'Configuration cannot be empty' });
        return;
      }
      JSON.parse(value);
      setIsJsonValid(true);
      setStatus({ type: '', message: '' });
    } catch (e) {
      setIsJsonValid(false);
      setStatus({ type: 'error', message: 'Invalid JSON format' });
    }
  };

  const handleGetConfig = async () => {
    if (!selectedModel) {
      setStatus({ type: 'error', message: 'Please select a model' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.getDefaultConfig(selectedModel);
      setConfigJson(JSON.stringify(response.config || response, null, 2));
      setStatus({ type: 'success', message: 'Default configuration retrieved successfully' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to get default configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    if (!selectedModel) {
      setStatus({ type: 'error', message: 'Please select a model' });
      return;
    }

    if (!configJson.trim()) {
      setStatus({ type: 'error', message: 'Configuration cannot be empty' });
      return;
    }

    if (!isJsonValid) {
      setStatus({ type: 'error', message: 'Invalid JSON format' });
      return;
    }

    setLoading(true);
    try {
      const config = JSON.parse(configJson);
      await api.setDefaultConfig({
        model: selectedModel,
        compatible: 'jnet2',
        config: config
      });
      setStatus({ type: 'success', message: 'Default configuration saved successfully' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save default configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSave = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Default Configuration
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Model</InputLabel>
          <Select
            value={selectedModel}
            onChange={handleModelChange}
            label="Select Model"
          >
            <MenuItem value="JuganuNode">JuganuNode</MenuItem>
            <MenuItem value="JuganuCAP">JuganuCAP</MenuItem>
            <MenuItem value="Nvidia">AI (Jetson-NX)</MenuItem>
            <MenuItem value="qrb5165-128GB">AI (qrb5165)</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ height: '400px', border: '1px solid #ccc', borderRadius: 1, mb: 3 }}>
          <Editor
            height="100%"
            defaultLanguage="json"
            value={configJson}
            onChange={handleEditorChange}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: 'on',
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </Box>

        {status.message && (
          <Alert severity={status.type} sx={{ mb: 2 }}>
            {status.message}
          </Alert>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleGetConfig}
            disabled={!selectedModel || loading || restoreLoading}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Loading...
              </>
            ) : (
              'Get Default Config'
            )}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveClick}
            disabled={!isJsonValid || !selectedModel || loading || restoreLoading || !configJson.trim()}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              'Save Default Config'
            )}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRestoreSelected}
            disabled={!selectedModel || loading || restoreLoading}
          >
            {restoreLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Restoring...
              </>
            ) : (
              'Restore Default Config'
            )}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleRestoreAll}
            disabled={loading || restoreLoading}
          >
            {restoreLoading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Restoring...
              </>
            ) : (
              'Restore All Default Configs'
            )}
          </Button>
        </Stack>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSave}
      >
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to save this configuration as the default for {selectedModel}? This will affect all new devices of this model.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave}>Cancel</Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={confirmRestoreOpen}
        onClose={() => setConfirmRestoreOpen(false)}
      >
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedModel
              ? `Restore default configuration for ${selectedModel} from local file to Firestore? This overwrites the stored default.`
              : 'Restore default configuration for ALL models from local files to Firestore? This overwrites stored defaults.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreOpen(false)}>Cancel</Button>
          <Button
            onClick={() => performRestore(selectedModel)}
            variant="contained"
            color="secondary"
            disabled={restoreLoading}
          >
            {restoreLoading ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DefaultConfig; 